/**
 * FormPickerPropertyEditor
 *
 * Property editor for the "form-picker" control type.
 * Allows users to configure Form Picker behavior through the property panel.
 *
 * 2 sections:
 *   1. General - Show/Hide buttons (Edit, Clear, Search)
 *   2. Editor Options - Container selector, tab visibility, panel visibility
 *
 * SRP: responsible ONLY for rendering the form-picker property panel.
 */

import React, { memo, useCallback, useMemo } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../registry/PropertyEditor.types';
import type { FormControl } from '@atlas-forms/types-js';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormPickerConfig {
  // General
  showEditButton?: boolean;
  showClearButton?: boolean;
  showSearchButton?: boolean;

  // Editor options
  editorContainerSelector?: string;
  editorDefaultTab?: 'designer' | 'json' | 'settings';
  editorHideDesignerTab?: boolean;
  editorHideJsonTab?: boolean;
  editorHideSettingsTab?: boolean;
  editorHideNavigationTabs?: boolean;
  editorHidePropertiesPanel?: boolean;
  editorHideFieldToolbox?: boolean;
  editorHideSaveButton?: boolean;
}

// ─── Styling Helpers ──────────────────────────────────────────────────────────

function makeInputStyle(isDark: boolean): React.CSSProperties {
  return {
    width: '100%',
    padding: '5px 8px',
    borderRadius: 5,
    border: `1px solid ${isDark ? 'rgba(71,85,105,0.6)' : 'rgba(71,85,105,0.5)'}`,
    background: isDark ? 'rgba(15,23,42,0.7)' : 'rgba(30,41,59,0.7)',
    color: '#ffffff',
    fontSize: 12,
    boxSizing: 'border-box' as const,
  };
}

function makeSelectStyle(isDark: boolean): React.CSSProperties {
  return {
    ...makeInputStyle(isDark),
    cursor: 'pointer',
    appearance: 'none' as const,
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 0.75rem center',
    backgroundSize: '16px 12px',
    paddingRight: '2.5rem',
  };
}

function makeLabelStyle(isDark: boolean): React.CSSProperties {
  return {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    marginBottom: 3,
    color: '#ffffff',
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  isDark: boolean;
  children: React.ReactNode;
  actionSlot?: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, isDark, children, actionSlot }) => (
  <div
    style={{
      marginBottom: 16,
      padding: '10px 12px',
      borderRadius: 6,
      background: isDark ? 'rgba(15,23,42,0.4)' : 'rgba(30,41,59,0.7)',
      border: `1px solid ${isDark ? 'rgba(51,65,85,0.5)' : 'rgba(71,85,105,0.4)'}`,
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 10,
      }}
    >
      <h4
        style={{
          margin: 0,
          fontSize: 12,
          fontWeight: 700,
          color: '#ffffff',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {title}
      </h4>
      {actionSlot}
    </div>
    {children}
  </div>
);

interface FieldProps {
  label: string;
  isDark: boolean;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, isDark, children }) => (
  <div style={{ marginBottom: 8 }}>
    <label style={makeLabelStyle(isDark)}>{label}</label>
    {children}
  </div>
);

interface ToggleProps {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  isDark: boolean;
}

const Toggle: React.FC<ToggleProps> = ({ label, checked, onChange, isDark }) => (
  <label
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      cursor: 'pointer',
      fontSize: 12,
      color: '#ffffff',
      marginBottom: 6,
      userSelect: 'none',
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      style={{ accentColor: '#309B48', cursor: 'pointer' }}
    />
    {label}
  </label>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export const FormPickerPropertyEditor: React.FC<PropertyEditorProps> = ({
  control,
  onConfigChange,
}) => {
  const config = useMemo(
    () => ((control.config ?? {}) as FormPickerConfig),
    [control.config],
  );

  const isDark =
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  const inputStyle = useMemo(() => makeInputStyle(isDark), [isDark]);
  const selectStyle = useMemo(() => makeSelectStyle(isDark), [isDark]);
  const labelStyle = useMemo(() => makeLabelStyle(isDark), [isDark]);

  const setConfig = useCallback(
    <K extends keyof FormPickerConfig>(key: K, value: FormPickerConfig[K]) => {
      onConfigChange(key as string, value);
    },
    [onConfigChange],
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '4px 0' }}>
      {/* Section 1: General */}
      <Section title="General" isDark={isDark}>
        <Toggle
          label="Show Edit Button"
          checked={config.showEditButton !== false}
          onChange={(v) => setConfig('showEditButton', v)}
          isDark={isDark}
        />
        <Toggle
          label="Show Clear Button"
          checked={config.showClearButton !== false}
          onChange={(v) => setConfig('showClearButton', v)}
          isDark={isDark}
        />
        <Toggle
          label="Show Search Button"
          checked={config.showSearchButton !== false}
          onChange={(v) => setConfig('showSearchButton', v)}
          isDark={isDark}
        />
      </Section>

      {/* Section 2: Editor Options */}
      <Section title="Editor Options" isDark={isDark}>
        <Field label="Container Selector (CSS)" isDark={isDark}>
          <input
            type="text"
            value={config.editorContainerSelector ?? ''}
            onChange={(e) => setConfig('editorContainerSelector', e.target.value || undefined)}
            placeholder="#editor-container"
            style={inputStyle}
            title="CSS selector for embedded rendering (e.g., #editor, .form-editor-area). Leave empty for modal."
          />
          <div
            style={{
              fontSize: 11,
              color: isDark ? '#94a3b8' : '#6b7280',
              marginTop: 4,
            }}
          >
            Leave empty for modal rendering
          </div>
        </Field>

        <Field label="Default Tab" isDark={isDark}>
          <select
            value={config.editorDefaultTab ?? 'designer'}
            onChange={(e) => setConfig('editorDefaultTab', e.target.value as any)}
            style={selectStyle}
          >
            <option value="designer">Designer</option>
            <option value="json">JSON</option>
            <option value="settings">Settings</option>
          </select>
        </Field>

        {/* Tab Visibility Section */}
        <div
          style={{
            marginTop: 10,
            marginBottom: 10,
            paddingTop: 10,
            borderTop: `1px solid ${isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0'}`,
          }}
        >
          <h5
            style={{
              margin: '0 0 10px',
              fontSize: 12,
              fontWeight: 700,
              color: isDark ? '#e2e8f0' : '#cbd5e1',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Hide Tabs
          </h5>
          <Toggle
            label="Hide Designer Tab"
            checked={config.editorHideDesignerTab ?? false}
            onChange={(v) => setConfig('editorHideDesignerTab', v || undefined)}
            isDark={isDark}
          />
          <Toggle
            label="Hide JSON Tab"
            checked={config.editorHideJsonTab ?? false}
            onChange={(v) => setConfig('editorHideJsonTab', v || undefined)}
            isDark={isDark}
          />
          <Toggle
            label="Hide Settings Tab"
            checked={config.editorHideSettingsTab ?? false}
            onChange={(v) => setConfig('editorHideSettingsTab', v || undefined)}
            isDark={isDark}
          />
          <Toggle
            label="Hide All Tabs"
            checked={config.editorHideNavigationTabs ?? false}
            onChange={(v) => setConfig('editorHideNavigationTabs', v || undefined)}
            isDark={isDark}
          />
        </div>

        {/* UI Elements Visibility Section */}
        <div
          style={{
            marginBottom: 10,
            paddingTop: 10,
            borderTop: `1px solid ${isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0'}`,
          }}
        >
          <h5
            style={{
              margin: '0 0 10px',
              fontSize: 12,
              fontWeight: 700,
              color: '#ffffff',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Hide UI Elements
          </h5>
          <Toggle
            label="Hide Properties Panel"
            checked={config.editorHidePropertiesPanel ?? false}
            onChange={(v) => setConfig('editorHidePropertiesPanel', v || undefined)}
            isDark={isDark}
          />
          <Toggle
            label="Hide Field Toolbox"
            checked={config.editorHideFieldToolbox ?? false}
            onChange={(v) => setConfig('editorHideFieldToolbox', v || undefined)}
            isDark={isDark}
          />
          <Toggle
            label="Hide Save Button"
            checked={config.editorHideSaveButton ?? false}
            onChange={(v) => setConfig('editorHideSaveButton', v || undefined)}
            isDark={isDark}
          />
        </div>
      </Section>

      {/* Help Text */}
      <div
        style={{
          padding: '8px 10px',
          borderRadius: 4,
          background: isDark ? 'rgba(48,155,72,0.08)' : 'rgba(30,41,59,0.7)',
          border: `1px solid ${isDark ? 'rgba(48,155,72,0.25)' : 'rgba(71,85,105,0.3)'}`,
          fontSize: 11,
          color: '#ffffff',
        }}
      >
        <strong style={{ fontWeight: 600 }}>💡 Tip:</strong> Use <code style={{ background: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)', padding: '2px 4px', borderRadius: 3, fontFamily: 'monospace', fontSize: '11px' }}>Container Selector</code> to embed the editor in a specific element. Otherwise, it opens in a modal.
      </div>
    </div>
  );
};

FormPickerPropertyEditor.displayName = 'FormPickerPropertyEditor';

// ─── PropertyEditor registry entry ───────────────────────────────────────────

export const FORM_PICKER_PROPERTY_EDITOR_TYPE = 'form-picker' as const;

/**
 * FormPickerPropertyEditorRegistration
 *
 * PropertyEditor object for use with PropertyEditorRegistry.register().
 * Bridges PropertyEditorProps (onConfigChange / onControlChange) to the
 * standalone FormPickerPropertyEditor FC.
 *
 * isDark is not available from PropertyEditorProps — detected from DOM.
 */
export const FormPickerPropertyEditorRegistration: PropertyEditor = {
  type: FORM_PICKER_PROPERTY_EDITOR_TYPE,
  label: 'Form Picker',
  description:
    'Configure Form Picker: button visibility, editor rendering, and UI options',
  version: '1.0.0',
  render: (props: PropertyEditorProps) => <FormPickerPropertyEditor {...props} />,
};

export { FormPickerPropertyEditor as default };
